import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Add, Remove } from './icons';
import { useFaqSection } from '../hooks/useFaqSection';
import { useGA4 } from '../hooks/useGA4';
import { getCurrentLocale } from '../services/api';

const FaqSection: React.FC = () => {
  const { data, loading, error } = useFaqSection();
  const [openIndex, setOpenIndex] = useState<number | null>(0); // First item open by default
  const locale = getCurrentLocale();
  const { trackEvent } = useGA4();

  // Fallback data
  const defaultData = {
    title: "Veel gestelde vragen",
    description: "Heeft u nog vragen? Stuur ons dan een whatsapp of mailberichtje. Even bellen kan natuurlijk ook maar als wij bezig zijn besteden we onze aandacht aan een client en nemen wij niet op. Wij bellen u dan later terug.",
    faqItems: [
      {
        question: "Doet het pijn?",
        answer: "De prikjes zijn klein en goed te verdragen. Meestal voel je vooral lichte druk of een tinteling in de huid waar de behandeling plaatsvindt. Je kan vooraf paracetemol nemen om de pijn wat meer te onderdrukken als je snel last hebt.",
        order: 1
      },
      {
        question: "Hoe snel zie ik resultaat?",
        answer: "De meeste cliënten zien minder haaruitval na 2-4 weken en nieuwe haargroei binnen 4-8 weken, afhankelijk van het behandelplan.",
        order: 2
      },
      {
        question: "Voor wie is deze behandeling geschikt?",
        answer: "De behandeling is geschikt voor mannen en vrouwen met verschillende vormen van haaruitval, waaronder androgenetische alopecia, telogeen effluvium en andere vormen van haarverlies.",
        order: 3
      },
      {
        question: "Zijn er risico's of bijwerkingen?",
        answer: "De behandeling is zeer veilig met minimale risico's. Mogelijke bijwerkingen zijn lichte roodheid, zwelling of gevoeligheid op de behandelde plek die meestal binnen enkele uren tot dagen verdwijnt.",
        order: 4
      },
      {
        question: "Wat als het niet werkt?",
        answer: "We bespreken tijdens de intake wat je kunt verwachten. Als de resultaten niet naar wens zijn, kunnen we het behandelplan aanpassen of andere opties bespreken.",
        order: 5
      }
    ]
  };

  const sectionData = data || defaultData;

  const toggleAccordion = (index: number) => {
    const isOpening = openIndex !== index;
    const faqItem = sectionData.faqItems[index];
    
    // Track FAQ interaction
    trackEvent('faq_toggle', {
      action: isOpening ? 'expand' : 'collapse',
      faq_question: faqItem?.question || `FAQ ${index + 1}`,
      faq_index: index
    });
    
    setOpenIndex(openIndex === index ? null : index);
  };

  if (loading) {
    return (
      <section className="faq-section" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <div className="faq-content">
          <div className="loading-placeholder">
            <div className="loading-title"></div>
          </div>
          <div className="faq-container">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="faq-item">
                <div className="loading-placeholder">
                  <div className="loading-title"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="faq-section" style={{ backgroundColor: '#F6F7F9' }} dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <div className="faq-content">
          <h2 className="faq-title" style={{ marginBottom: '84px' }}>{defaultData.title}</h2>
          <div className="faq-container">
            {defaultData.faqItems.map((item, index) => (
              <div key={index} className="faq-item">
                <button 
                  className="faq-question"
                  onClick={() => toggleAccordion(index)}
                >
                  <span>{item.question}</span>
                  {openIndex === index ? <Remove size={20} /> : <Add size={20} />}
                </button>
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div 
                      className="faq-answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <p>{item.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="faq-section" style={{ backgroundColor: '#F6F7F9' }} dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <div className="faq-content">
        <motion.h2 
          className="faq-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {sectionData.title}
        </motion.h2>
        
        {sectionData.description && (
          <motion.p 
            className="faq-description"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {sectionData.description}
          </motion.p>
        )}
        
        <motion.div 
          className="faq-container"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {(sectionData.faqItems || [])
            .sort((a, b) => a.order - b.order)
            .map((item, index) => (
            <motion.div 
              key={index} 
              className="faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
            >
              <button 
                className="faq-question"
                onClick={() => toggleAccordion(index)}
              >
                <span>{item.question}</span>
                {openIndex === index ? <Remove size={20} /> : <Add size={20} />}
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div 
                    className="faq-answer"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p>{item.answer}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.p 
          className="faq-bottom-description"
          style={{ textAlign: 'center', marginTop: '40px' }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          Heeft u nog vragen? Stuur ons dan een whatsapp of mailberichtje. Even bellen kan natuurlijk ook maar als wij bezig zijn besteden we onze aandacht aan een client en nemen wij niet op. Wij bellen u dan later terug.
        </motion.p>
      </div>
    </section>
  );
};

export default FaqSection; 